/**
 * Stand-in content for previews of a TEMPLATE (no project behind it): a
 * small apartment as plan entities, a gray density pattern and placeholder
 * values that look like a real sheet.
 */

import type { DxfEntity } from '@pointorama/dxf-exporter';
import type { DocumentLayoutInput } from '@pointorama/print-engine';
import { lengthIn, type PrintFloorInfo, type PrintLengthUnit } from 'shared-types';

export const SAMPLE_FLOORS: PrintFloorInfo[] = [{ id: 'sample-ground', name: 'Ground floor', elevation: 0 }];

export const SAMPLE_SOURCES: DocumentLayoutInput['sources'] = {
  project: { name: 'Residence Parkview', number: '2026-014', address: 'Parklaan 12, 2000 Antwerpen', client: 'Parkview NV' },
  org: { name: 'Your organisation', address: 'Street 1, 1000 City', phone: '+32 3 000 00 00', email: 'info@example.com', website: 'www.example.com', vat: 'BE0123.456.789' },
  sheet: { name: 'Floor plan', number: 'A-101' },
  user: { name: 'Jane Architect' },
};

const p = (x: number, y: number) => ({ x, y, z: 0 });

/** The sample rooms (inside wall faces, meters): name, outline box, where the tag sits. */
const SAMPLE_ROOMS: readonly { name: string; minX: number; minY: number; maxX: number; maxY: number }[] = [
  { name: 'LIVING', minX: -5.7, minY: -3.7, maxX: 0.5, maxY: 3.7 },
  { name: 'BEDROOM', minX: 0.6, minY: -0.5, maxX: 5.7, maxY: 3.7 },
  { name: 'KITCHEN', minX: 0.6, minY: -3.7, maxX: 5.7, maxY: -0.5 },
];

/**
 * A 12 × 8 m apartment: outer walls, partitions, a door with its swing, room
 * tags and a dimension. Areas and lengths are written in the user's unit (as
 * the project's own plan labels are); geometry stays in meters. Each room tag
 * is ONE two-line text, like the print plan's (lines stack on the page at any
 * view rotation).
 */
export function samplePlanEntities(unit: PrintLengthUnit = 'm'): DxfEntity[] {
  const area = (room: (typeof SAMPLE_ROOMS)[number]) => lengthIn(1, unit) ** 2 * (room.maxX - room.minX) * (room.maxY - room.minY);
  return [
    // Outer wall band (outside and inside faces).
    { type: 'POLYLINE', points: [p(-6, -4), p(6, -4), p(6, 4), p(-6, 4)], closed: true, layerName: 'Walls' },
    { type: 'POLYLINE', points: [p(-5.7, -3.7), p(5.7, -3.7), p(5.7, 3.7), p(-5.7, 3.7)], closed: true, layerName: 'Walls' },
    // Partitions.
    { type: 'POLYLINE', points: [p(0.5, -3.7), p(0.5, 0.6)], closed: false, layerName: 'Walls' },
    { type: 'POLYLINE', points: [p(0.6, -3.7), p(0.6, 0.6)], closed: false, layerName: 'Walls' },
    { type: 'POLYLINE', points: [p(0.5, 1.5), p(0.5, 3.7)], closed: false, layerName: 'Walls' },
    { type: 'POLYLINE', points: [p(0.6, 1.5), p(0.6, 3.7)], closed: false, layerName: 'Walls' },
    { type: 'POLYLINE', points: [p(0.6, -0.5), p(5.7, -0.5)], closed: false, layerName: 'Walls' },
    // Door in the partition: leaf + quarter swing.
    { type: 'LINE', start: p(0.55, 0.6), end: p(1.45, 0.6), layerName: 'Openings' },
    { type: 'POLYLINE', points: [p(1.45, 0.6), p(0.55, 1.5)], bulges: [Math.tan(Math.PI / 8)], closed: false, layerName: 'Openings' },
    // Room tags: name and area, centered in the room.
    ...SAMPLE_ROOMS.map(
      (room): DxfEntity => ({
        type: 'TEXT',
        position: p((room.minX + room.maxX) / 2, (room.minY + room.maxY) / 2),
        text: `${room.name}\n${area(room).toFixed(1)} ${unit}²`,
        height: 0.2,
        hAlign: 1,
        vAlign: 2,
        layerName: 'RoomTags',
      }),
    ),
    // Overall dimension below the plan.
    { type: 'LINE', start: p(-6, -4.8), end: p(6, -4.8), layerName: 'Dimensions' },
    { type: 'LINE', start: p(-6, -4.6), end: p(-6, -5), layerName: 'Dimensions' },
    { type: 'LINE', start: p(6, -4.6), end: p(6, -5), layerName: 'Dimensions' },
    { type: 'TEXT', position: p(0, -4.6), text: lengthIn(12, unit).toFixed(2), height: 0.15, hAlign: 1, vAlign: 0, layerName: 'Dimensions' },
  ];
}

/** World extent of the sample plan (for framing previews). */
export const SAMPLE_PLAN_BOUNDS = { minX: -6.5, minY: -5.5, maxX: 6.5, maxY: 4.5 } as const;
